// gift-unlock.js — Sense/Dawn
// Variant-based gift unlock UI + SAME-REQUEST gift add (FormData patch)
// + Cart reconciler so gifts always match total eligible quantities in cart
//
// Behavior:
// - On product page: inject gifts into the SAME /cart/add request (drawer opens once).
// - After any cart change: reconcile gift quantities so they match the cart totals.
//   Example: add 1x 256GB and 1x 1TB => gift-1 qty 2, gift-2 qty 1, gift-3 qty 1.

(function () {
  "use strict";

  /**
   * =========================
   * CONFIG
   * =========================
   */
  const DEBUG = true;
  const log = (...args) => DEBUG && console.log("[GiftUnlock]", ...args);

  // Capacity -> which gifts unlock
  const GIFT_UNLOCK_CONFIG = {
    "128GB": [],
    "256GB": ["gift-1"],
    "512GB": ["gift-1", "gift-2"],
    "1TB": ["gift-1", "gift-2", "gift-3"],
    "2TB": ["gift-1", "gift-2", "gift-3"],
  };

  // Gift IDs -> Gift VARIANT IDs
  const GIFT_VARIANT_IDS = {
    "gift-1": 50825948135733,
    "gift-2": 50825948299573,
    "gift-3": 52033211662645,
  };

  // How we detect “capacity” from cart line item text
  // (Adjust if your store uses different formatting)
  function extractCapacityToken(str) {
    const s = String(str || "").toUpperCase().replace(/\s+/g, "");
    const m = s.match(/(\d+)(GB|TB)/);
    return m ? `${m[1]}${m[2]}` : null;
  }

  /**
   * =========================
   * SMALL HELPERS
   * =========================
   */
  function debounce(fn, wait = 160) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function getProductJson() {
    const dawn = document.querySelector('script[type="application/json"][id^="ProductJson-"]');
    if (dawn?.textContent) {
      try {
        return JSON.parse(dawn.textContent);
      } catch (e) {
        log("Failed to parse ProductJson-* script", e);
      }
    }

    const dataJson = document.querySelector("[data-product-json]");
    if (dataJson?.textContent) {
      try {
        return JSON.parse(dataJson.textContent);
      } catch (e) {
        log("Failed to parse [data-product-json]", e);
      }
    }

    if (window.ShopifyAnalytics?.meta?.product) return window.ShopifyAnalytics.meta.product;
    return null;
  }

  function getSelectedVariantId() {
    const input = document.querySelector('input[name="id"]');
    if (input?.value) return input.value;

    const sel = document.querySelector('select[name="id"]');
    if (sel?.value) return sel.value;

    const radio = document.querySelector('input[name="id"]:checked');
    if (radio?.value) return radio.value;

    return null;
  }

  function readSelectedOptionsFromDom() {
    const els = document.querySelectorAll(
      'variant-selects select, variant-radios input[type="radio"]:checked, [name^="options["], input[type="radio"]:checked, select'
    );

    return Array.from(els)
      .map((el) => {
        if (el.tagName === "SELECT") return el.value;
        if (el.type === "radio" && el.checked) return el.value;
        return null;
      })
      .filter(Boolean);
  }

  // Convert "properties[Gift]" keys to name "Gift"
  function parsePropertyKey(k) {
    const m = String(k).match(/^properties\[(.+)\]$/);
    return m ? m[1] : null;
  }

  // Reverse map: variantId -> giftId (gift-1/2/3)
  const GIFT_ID_BY_VARIANT = Object.fromEntries(
    Object.entries(GIFT_VARIANT_IDS).map(([giftId, vid]) => [String(vid), giftId])
  );

  async function fetchCart() {
    const res = await fetch("/cart.js", { credentials: "same-origin" });
    if (!res.ok) throw new Error("Failed to fetch /cart.js");
    return res.json();
  }

  async function cartAddJson(payload) {
    const res = await fetch("/cart/add.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const txt = await res.text().catch(() => "");
    if (!res.ok) throw new Error(txt || "cart/add failed");
    try {
      return JSON.parse(txt);
    } catch {
      return txt;
    }
  }

  async function cartChangeByKey(key, quantity) {
    const res = await fetch("/cart/change.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: key, quantity }),
    });
    const txt = await res.text().catch(() => "");
    if (!res.ok) throw new Error(txt || "cart/change failed");
    try {
      return JSON.parse(txt);
    } catch {
      return txt;
    }
  }

  /**
   * =========================
   * Gift Unlock Manager (UI)
   * =========================
   */
  class GiftUnlockManager {
    constructor() {
      this.product = null;
      this.currentVariant = null;
      this.scheduleDetect = debounce(() => this.detectVariantChange(), 140);
      this.init();
    }

    init() {
      this.product = getProductJson();
      if (!this.product) log("No product JSON found. Will rely on DOM option parsing.");

      document.addEventListener(
        "change",
        () => {
          this.scheduleDetect();
          setTimeout(() => this.detectVariantChange(), 250);
          setTimeout(() => this.detectVariantChange(), 650);
        },
        true
      );

      document.addEventListener("variant:change", () => {
        this.scheduleDetect();
        setTimeout(() => this.detectVariantChange(), 250);
      });

      const target = document.querySelector("main") || document.body;
      const obs = new MutationObserver(() => {
        if (!this.product) this.product = getProductJson();
        this.scheduleDetect();
      });
      obs.observe(target, { childList: true, subtree: true });

      this.detectVariantChange();
    }

    detectVariantChange() {
      const variantId = getSelectedVariantId();
      let variant = null;

      if (!this.product) this.product = getProductJson();

      if (variantId && this.product?.variants?.length) {
        const v = this.product.variants.find((x) => String(x.id) === String(variantId));
        if (v) variant = v;
      }

      if (variant) {
        const optionsArray =
          Array.isArray(variant.options) && variant.options.length
            ? variant.options
            : readSelectedOptionsFromDom();

        const title = variant.title || (optionsArray.length ? optionsArray.join(" / ") : "");

        this.currentVariant = {
          ...variant,
          title,
          option1: variant.option1 ?? optionsArray[0],
          option2: variant.option2 ?? optionsArray[1],
          option3: variant.option3 ?? optionsArray[2],
        };
      } else {
        const opts = readSelectedOptionsFromDom();
        this.currentVariant = {
          id: variantId || null,
          title: opts.join(" / "),
          option1: opts[0] || null,
          option2: opts[1] || null,
          option3: opts[2] || null,
        };
      }

      this.updateGiftStates();
    }

    getCapacityToken() {
      const candidates = [
        this.currentVariant?.option1,
        this.currentVariant?.option2,
        this.currentVariant?.option3,
        this.currentVariant?.title,
      ].filter(Boolean);

      for (const c of candidates) {
        const token = extractCapacityToken(c);
        if (token && Object.prototype.hasOwnProperty.call(GIFT_UNLOCK_CONFIG, token)) return token;
      }
      return null;
    }

    getUnlockedGiftIds() {
      const cap = this.getCapacityToken();
      return cap ? GIFT_UNLOCK_CONFIG[cap] : [];
    }

    getUnlockedGiftVariantIds() {
      const giftIds = this.getUnlockedGiftIds();
      return giftIds.map((gid) => Number(GIFT_VARIANT_IDS[gid])).filter(Boolean);
    }

    updateGiftStates() {
      const giftItems = document.querySelectorAll(".gift-unlock-item");
      const cap = this.getCapacityToken();
      const unlockedGifts = cap ? GIFT_UNLOCK_CONFIG[cap] : [];

      if (DEBUG) log("Capacity:", cap, "Unlocked gifts:", unlockedGifts);

      giftItems.forEach((giftItem, index) => {
        const giftId = giftItem.dataset.giftId || `gift-${index + 1}`;
        const checkbox = giftItem.querySelector(".gift-checkbox");
        const unlocked = unlockedGifts.includes(giftId);

        giftItem.classList.toggle("is-unlocked", unlocked);
        giftItem.classList.toggle("is-locked", !unlocked);
        giftItem.style.opacity = unlocked ? "1" : "0.55";

        if (checkbox) {
          checkbox.checked = unlocked;
          checkbox.disabled = !unlocked;
        }
      });
    }
  }

  // Start manager (product page UI)
  const start = () => (window.__giftUnlockManager = new GiftUnlockManager());
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();

  /**
   * =========================
   * 1) SAME-REQUEST injection (FormData patch)
   * =========================
   * This ensures the drawer opens once with gifts already included for THIS add.
   * (Cart reconciler will adjust totals later if multiple items exist.)
   */
  const OriginalFormData = window.FormData;

  window.FormData = function PatchedFormData(form) {
    const fd = new OriginalFormData(form);

    try {
      if (!(form instanceof HTMLFormElement)) return fd;
      if (!form.closest("product-form")) return fd;

      const mainId = fd.get("id");
      if (!mainId) return fd;

      const mgr = window.__giftUnlockManager;
      if (!mgr || typeof mgr.getUnlockedGiftVariantIds !== "function") return fd;

      mgr.detectVariantChange?.();

      const giftVariantIds = mgr.getUnlockedGiftVariantIds();
      if (!giftVariantIds.length) return fd;

      if (fd.has("items[0][id]") || fd.has("items[][id]")) return fd;

      const qty = Math.max(1, parseInt(String(fd.get("quantity") || "1"), 10) || 1);

      // For this click: gifts match the main qty.
      const giftQty = qty;

      const entries = Array.from(fd.entries());

      fd.delete("id");
      fd.delete("quantity");

      for (const [k] of entries) {
        if (String(k).startsWith("properties[")) fd.delete(k);
        if (String(k) === "selling_plan") fd.delete(k);
      }

      // Main
      fd.append("items[0][id]", String(mainId));
      fd.append("items[0][quantity]", String(qty));

      // Main properties + selling_plan
      for (const [k, v] of entries) {
        const propName = parsePropertyKey(k);
        if (propName) {
          fd.append(`items[0][properties][${propName}]`, v);
        } else if (String(k) === "selling_plan") {
          fd.append("items[0][selling_plan]", v);
        }
      }

      // Gifts
      giftVariantIds.forEach((vid, idx) => {
        const i = idx + 1;
        fd.append(`items[${i}][id]`, String(vid));
        fd.append(`items[${i}][quantity]`, String(giftQty));
        fd.append(`items[${i}][properties][_auto_gift]`, "true");
      });

      log("Injected gifts into SAME request (click-level):", {
        mainId: Number(mainId),
        qty,
        gifts: giftVariantIds,
        giftQty,
      });
    } catch (e) {
      console.error("[GiftUnlock] FormData patch error:", e);
    }

    return fd;
  };

  window.FormData.prototype = OriginalFormData.prototype;

  /**
   * =========================
   * 2) CART RECONCILER (multi-variant / multi-add support)
   * =========================
   * Ensures gifts in cart always equal totals across the whole cart.
   */
  let reconcileInProgress = false;

  function isGiftCartItem(item) {
    return Boolean(item?.properties && item.properties._auto_gift);
  }

  function detectCapacityFromCartItem(item) {
    // We try the most likely strings in cart.js
    const candidates = [
      item?.variant_title,
      item?.title,
      item?.product_title,
      item?.final_price, // ignore but harmless
    ].filter(Boolean);

    // Also include options if present (some themes include item.options_with_values)
    if (Array.isArray(item?.options_with_values)) {
      for (const ov of item.options_with_values) {
        if (ov?.value) candidates.push(ov.value);
      }
    }

    for (const c of candidates) {
      const token = extractCapacityToken(c);
      if (token && Object.prototype.hasOwnProperty.call(GIFT_UNLOCK_CONFIG, token)) return token;
    }
    return null;
  }

  function computeRequiredGiftQuantities(cart) {
    // giftId -> requiredQty
    const required = Object.fromEntries(Object.keys(GIFT_VARIANT_IDS).map((g) => [g, 0]));

    for (const item of cart.items || []) {
      if (isGiftCartItem(item)) continue;

      const cap = detectCapacityFromCartItem(item);
      if (!cap) continue;

      const giftsForCap = GIFT_UNLOCK_CONFIG[cap] || [];
      for (const giftId of giftsForCap) {
        required[giftId] = (required[giftId] || 0) + Number(item.quantity || 0);
      }
    }

    return required;
  }

  async function reconcileGiftsInCart() {
    if (reconcileInProgress) return;

    reconcileInProgress = true;
    try {
      const cart = await fetchCart();

      const required = computeRequiredGiftQuantities(cart);
      if (DEBUG) log("Reconcile required gift qty:", required);

      // Existing gift lines: giftId -> { key, qty }
      const existing = {};
      for (const item of cart.items || []) {
        if (!isGiftCartItem(item)) continue;

        const giftId = GIFT_ID_BY_VARIANT[String(item.id)];
        if (!giftId) continue;

        // If somehow duplicates exist for same giftId, we’ll keep the first and let you handle cleanup later.
        if (!existing[giftId]) {
          existing[giftId] = { key: item.key, qty: Number(item.quantity || 0), variantId: Number(item.id) };
        }
      }

      // 1) Update existing gift quantities (or remove)
      for (const giftId of Object.keys(GIFT_VARIANT_IDS)) {
        const wantQty = Number(required[giftId] || 0);
        const ex = existing[giftId];

        if (ex) {
          if (ex.qty !== wantQty) {
            if (DEBUG) log("Changing gift", giftId, "from", ex.qty, "to", wantQty);
            await cartChangeByKey(ex.key, wantQty);
          }
        } else {
          // Missing gift line: add it if needed
          if (wantQty > 0) {
            const variantId = Number(GIFT_VARIANT_IDS[giftId]);
            if (DEBUG) log("Adding missing gift", giftId, "qty", wantQty, "variant", variantId);
            await cartAddJson({
              id: variantId,
              quantity: wantQty,
              properties: { _auto_gift: "true" },
            });
          }
        }
      }
    } catch (e) {
      console.error("[GiftUnlock] reconcileGiftsInCart error:", e);
    } finally {
      reconcileInProgress = false;
    }
  }

  const scheduleReconcile = debounce(() => reconcileGiftsInCart(), 200);

  // Trigger reconcile after any cart mutation request
  function isCartMutationUrl(url) {
    if (!url) return false;
    return (
      url.includes("/cart/add") ||
      url.includes("/cart/change") ||
      url.includes("/cart/update") ||
      url.includes("/cart/clear")
    );
  }

  // Hook fetch
  const originalFetch = window.fetch.bind(window);
  window.fetch = async function (input, init) {
    const url = typeof input === "string" ? input : (input && input.url) || "";
    const res = await originalFetch(input, init);

    try {
      if (res?.ok && isCartMutationUrl(url)) {
        // Avoid responding to our own reconcile writes
        if (!reconcileInProgress) scheduleReconcile();
      }
    } catch (_) {}

    return res;
  };

  // Hook XHR
  const OriginalXHR = window.XMLHttpRequest;
  window.XMLHttpRequest = function PatchedXHR() {
    const xhr = new OriginalXHR();
    let requestUrl = "";

    const originalOpen = xhr.open;
    xhr.open = function (method, url) {
      requestUrl = String(url || "");
      return originalOpen.apply(xhr, arguments);
    };

    xhr.addEventListener("load", function () {
      try {
        if (xhr.status >= 200 && xhr.status < 300 && isCartMutationUrl(requestUrl)) {
          if (!reconcileInProgress) scheduleReconcile();
        }
      } catch (_) {}
    });

    return xhr;
  };

  // Optional: run once on page load (useful on cart page)
  setTimeout(() => scheduleReconcile(), 600);
})();